/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { Table, ActionMenu } from '../../design-system';
import { Card, Input, message, Modal as AntModal, Button as AntButton, Space, Typography } from 'antd';
import { PlusOutlined, SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { officeService } from '../../services/officeService';

const { Title } = Typography;

const OfficeList = () => {
    const navigate = useNavigate();
    const cookies = new Cookies();
    const user = cookies.get('userR');
    const [offices, setOffices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [search, setSearch] = useState('');

    // Check if user has access to all offices
    const hasAccessAll = user?.roles?.includes('ROLE_ACCESS_ALL_OFFICE') ||
                         user?.roles?.includes('ROLE_OWNER');

    useEffect(() => {
        // If user doesn't have access to all offices, redirect to their office
        if (!hasAccessAll && user?.office_id) {
            navigate(`/admin/office/${user.office_id}`);
            return;
        }

        loadOffices();
    }, [pagination.current, search]); // Reload when page or search changes

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1
        setSearch(value);
    };

    const handleSearchClear = () => {
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1
        setSearch('');
    };

    const loadOffices = async () => {
        setLoading(true);
        try {
            const result = await officeService.getOffices({
                page: pagination.current,
                limit: pagination.pageSize,
                search
            });

            setOffices(result.offices || []);
            setPagination(prev => ({ ...prev, total: result.total || 0 }));
        } catch (error) {
            message.error('Failed to load offices');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (officeId) => {
        navigate(`/admin/office/${officeId}`);
    };

    const handleDelete = (officeId, officeName, userCount) => {
        if (userCount > 0) {
            message.error('Cannot delete office with assigned users');
            return;
        }

        AntModal.confirm({
            title: 'Delete Office',
            content: `Are you sure you want to delete "${officeName}"?`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await officeService.deleteOffice(officeId);
                    message.success('Office deleted successfully');
                    loadOffices();
                } catch (error) {
                    if (error.response?.data?.code === 'OFFICE_HAS_USERS') {
                        message.error('Cannot delete office with assigned users');
                    } else {
                        message.error('Failed to delete office');
                    }
                }
            }
        });
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'full_name',
            key: 'full_name',
            width: '15%'
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            width: '25%',
            ellipsis: true
        },
        {
            title: 'Phone',
            dataIndex: 'contact_phone',
            key: 'contact_phone',
            width: '12%'
        },
        {
            title: 'Employees',
            key: 'user_count',
            width: '10%',
            align: 'center',
            render: (_, record) => record._count?.user || 0
        },
        {
            title: 'Hourly Rate',
            key: 'hourly_rate',
            width: '12%',
            align: 'right',
            render: (_, record) => {
                // Display hourly rate in USD if available
                if (record.currency && record.hourly_rate_usd) {
                    return `$${parseFloat(record.hourly_rate_usd).toFixed(2)}`;
                }
                return '-';
            }
        },
        {
            title: 'Fully Vaccinated',
            key: 'fully_vaccinated',
            width: '14%',
            align: 'center',
            render: (_, record) => {
                const fullyVaccinated = record.fully_vaccinated_count || 0;
                const totalUsers = record._count?.user || 0;
                if (totalUsers === 0) return '0% (0)';
                const percentage = Math.round((fullyVaccinated / totalUsers) * 100);
                return `${percentage}% (${fullyVaccinated})`;
            }
        },
        {
            title: '',
            key: 'actions',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <ActionMenu
                    items={['edit', 'delete']}
                    record={record}
                    onAction={(key, record) => {
                        if (key === 'edit') {
                            handleEdit(record.id);
                        } else if (key === 'delete') {
                            handleDelete(record.id, record.full_name, record._count?.user || 0);
                        }
                    }}
                    disabledKeys={(record._count?.user || 0) > 0 ? ['delete'] : []}
                />
            )
        }
    ];

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    if (!hasAccessAll) {
        return null; // Will redirect in useEffect
    }

    return (
        <div className="wrapper-grey finance-container">
            <Card>
                {/* Header with Title */}
                <div className="section-header" style={{ display: 'block' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 16,
                            flexWrap: 'wrap'
                        }}
                    >
                        <Title level={4} style={{ margin: 0 }}>OFFICES</Title>
                        <Space size="small" wrap style={{ justifyContent: 'flex-end' }}>
                            <AntButton
                                className="btn-white-add"
                                icon={<PlusOutlined />}
                                onClick={() => navigate('/admin/office/new')}
                            >
                                ADD OFFICE
                            </AntButton>

                            <Input
                                placeholder="Search"
                                style={{ width: 250, maxWidth: '100%' }}
                                prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                                suffix={
                                    search ? (
                                        <CloseCircleOutlined
                                            style={{
                                                color: "rgba(0,0,0,.25)",
                                                cursor: "pointer",
                                            }}
                                            onClick={handleSearchClear}
                                        />
                                    ) : null
                                }
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </Space>
                    </div>
                </div>

                {/* Table */}
                <Table
                    columns={columns}
                    dataSource={offices}
                    loading={loading}
                    rowKey="id"
                    pagination={pagination}
                    onChange={handleTableChange}
                    variant="bordered"
                />
            </Card>
        </div>
    );
};

export default OfficeList;
